import { diasTabelaHorario } from '@/templates/Cadastro/TabelaHorario/constants/dias-tabela-horario'
import { PreviewEspelho, zod } from '@acme/core'
import { z } from 'zod'
import ApiService from '../api.service'
import { Funcionario } from './funcionario'

const timeOrEmpty = z
    .string()
    .trim()
    .regex(/^$|^([01]?[0-9]|2[0-3]):[0-5][0-9]$/)
    .nullish()

const horariosSchema = z
    .object({
        label: z.string().optional(),
        Ent1: timeOrEmpty,
        Ent2: timeOrEmpty,
        Ent3: timeOrEmpty,
        Sai1: timeOrEmpty,
        Sai2: timeOrEmpty,
        Sai3: timeOrEmpty,
        Tot: timeOrEmpty,
    })
    .refine(
        data => {
            const { Ent1, Ent2, Ent3, Sai1, Sai2, Sai3 } = data
            const entradas = [Ent1, Ent2, Ent3].filter(Boolean)
            const saidas = [Sai1, Sai2, Sai3].filter(Boolean)

            return entradas.length === saidas.length
        },
        data => {
            const { Ent1, Ent2, Ent3, Sai1, Sai2, Sai3 } = data

            const entradas = [Ent1, Ent2, Ent3].filter(Boolean)
            const saidas = [Sai1, Sai2, Sai3].filter(Boolean)

            if (entradas.length < saidas.length) {
                return {
                    path: [`Ent${entradas.length + 1}`],
                }
            }

            return {
                path: [`Sai${saidas.length + 1}`],
            }
        },
    )
    .transform(({ label, Tot, ...entSai }) => ({
        label: label || '',
        ...entSai,
        Tot: Tot ?? '00:00',
    }))

const timeInput = timeOrEmpty.transform(v => v || '00:00')

// Campo opcional: string vazia (input não preenchido) vira undefined para
// não esbarrar no @Matches do DTO da API.
const timeOptional = timeOrEmpty.transform(v => v || undefined)

const toleranciasSchema = z.object({
    entradaAntes: timeInput,
    entradaDepois: timeInput,
    saidaAntes: timeInput,
    saidaDepois: timeInput,
    toleranciaTurno: timeInput.default('00:00'),
    toleranciaDia: timeInput.default('00:00'),
})

function isJSON(obj: any) {
    try {
        if (typeof obj === 'string') JSON.parse(obj)
        else JSON.stringify(obj)
        return true
    } catch (_) {
        return false
    }
}

const TabelaHorariosBaseSchemaObject = z.object({
    nome: z.string().min(1, 'Descrição é obrigatório'),
    tipo: zod.TabelaHorarioSchema.shape.tipo.default('DIARIA'),
    totSem: z.string(),
    toleranciaSemana: timeInput.default('00:00'),
    limiteDiario: timeOptional,
    intervaloDesconto: timeOptional,
    intervaloAposHoras: timeOptional,
    horarios: z.array(horariosSchema).min(7),
    codigo: z.coerce.number().nullish(),
    tolerancias: toleranciasSchema,
    diaDescontoDSR: zod.TabelaHorarioSchema.shape.diaDescontoDSR,
    previewEspelho: z
        .any()
        .optional()
        .transform(d => {
            if (!d) return undefined
            if (!isJSON(d)) return undefined
            return typeof d !== 'string' ? JSON.stringify(d) : d
        }),
    tempoPausas: zod.TabelaHorarioSchema.shape.tempoPausas.default('00:00'),
    pausasAcumulam: zod.TabelaHorarioSchema.shape.pausasAcumulam.default('N'),
    maximoPausas: zod.TabelaHorarioSchema.shape.maximoPausas.default(0),
    horasExtraPausas:
        zod.TabelaHorarioSchema.shape.horasExtraPausas.default('N'),
    faltaPausas: zod.TabelaHorarioSchema.shape.faltaPausas.default('N'),
    horasIntervalares:
        zod.TabelaHorarioSchema.shape.horasIntervalares.transform(
            v => v?.trim() || null,
        ),
})

const pausasAcumulamValidation = (
    data: z.infer<typeof TabelaHorariosBaseSchemaObject>,
) => {
    // Se pausas acumulam está ativo, deve ter tolerância por dia
    if (data.pausasAcumulam === 'S') {
        return (
            data.tolerancias.toleranciaDia &&
            data.tolerancias.toleranciaDia !== '00:00'
        )
    }
    return true
}

const TabelaHorariosBaseSchema = TabelaHorariosBaseSchemaObject.refine(
    pausasAcumulamValidation,
    {
        message: 'Pausas acumuladas requerem tolerância por dia configurada',
        path: ['tolerancias', 'toleranciaDia'],
    },
)

const TabelaHorariosSchema = TabelaHorariosBaseSchema

export const CreateTabelaHorariosSchema = TabelaHorariosSchema.transform(
    obj => {
        const horarios = obj.horarios as z.output<typeof horariosSchema>[]
        for (let i = 0; i < horarios.length; i++) {
            horarios[i].label = diasTabelaHorario[i]
        }
        return { ...obj, horarios }
    },
)

export const UpdateTabelaHorariosSchema = TabelaHorariosBaseSchemaObject.extend(
    {
        id: z.number(),
    },
)
    .refine(pausasAcumulamValidation, {
        message: 'Pausas acumuladas requerem tolerância por dia configurada',
        path: ['tolerancias', 'toleranciaDia'],
    })
    .transform(
        (obj: z.infer<typeof TabelaHorariosBaseSchema> & { id: number }) => {
            const horarios = obj.horarios as z.output<typeof horariosSchema>[]
            for (let i = 0; i < horarios.length; i++) {
                horarios[i].label = diasTabelaHorario[i]
                horarios[i].Tot = '' // Calcular no back-end
            }
            // Na carga semanal o total é informado pelo usuário; na diária o
            // back-end recalcula a partir dos horários.
            const totSem = obj.tipo === 'SEMANAL' ? obj.totSem : ''
            return { ...obj, horarios, totSem }
        },
    )

export const CreateTabelaHorarioFuncionarioSchema = z.object({
    empresaId: z.coerce.number(),
    funcionarioId: z.coerce.number(),
    tabelaHorarioId: z.coerce.number().positive(),
    dtInicial: z.coerce.date(),
    dtFinal: z.coerce
        .string()
        .optional()
        .nullish()
        .transform(v => (v ? new Date(v) : null))
        .refine(
            data => {
                if (!data) return true
                return data.toString() !== 'Invalid Date'
            },
            {
                message: 'Data final inválida',
            },
        ),
})

export const UpdateTabelaHorarioFuncionarioSchema =
    CreateTabelaHorarioFuncionarioSchema.pick({
        dtFinal: true,
        dtInicial: true,
    }).extend({
        id: z.coerce.number(),
    })

export type CreateTabelaHorariosInput = z.input<
    typeof CreateTabelaHorariosSchema
>
export type TabelaHorarios = z.output<typeof UpdateTabelaHorariosSchema> & {
    id: number
    previewEspelho?: PreviewEspelho
    horarios: z.output<typeof horariosSchema>[]
}
export type TabelaHorarioFuncionario = z.infer<
    typeof zod.TabelaHorarioFuncionarioSchema
> & {
    isTrocaAutomatica?: boolean
    TabelaHorario: TabelaHorarios
    Funcionario?: Funcionario
}

const service = new ApiService<CreateTabelaHorariosInput, TabelaHorarios>(
    'tabela-horarios',
)

const funcionario = new ApiService<
    TabelaHorarioFuncionario,
    TabelaHorarioFuncionario,
    {
        funcionarioId: number
        dataInicial: string
        dataFinal: string
    }
>('tabela-horarios/funcionario')

export const tabelaHorarios = Object.assign(service, { funcionario })
